import { useState } from 'react';
import { getValidationErrorMessage } from '../helpers';

/**
 * Custom hook for handling form validation in modals
 * Provides consistent validation error handling and helper methods
 *
 * @param {array} fieldNames - Array of field names to track validation for
 * @returns {object} - Validation state and helper methods
 */
export function useFormValidation(fieldNames = []) {
  const [formValidationErrors, setFormValidationErrors] = useState(null);

  // Create error getters for each field
  const errors = {};
  fieldNames.forEach(fieldName => {
    errors[`${fieldName}Error`] = getValidationErrorMessage(formValidationErrors, fieldName);
  });

  // Helper methods
  const clearError = (fieldName) => {
    if (formValidationErrors && formValidationErrors[fieldName]) {
      setFormValidationErrors({
        ...formValidationErrors,
        [fieldName]: null,
      });
    }
  };

  const clearAllErrors = () => {
    setFormValidationErrors(null);
  };

  const setErrors = (errors) => {
    setFormValidationErrors(errors);
  };

  const hasErrors = () => {
    return formValidationErrors && Object.values(formValidationErrors).some(error => error);
  };

  return {
    formValidationErrors,
    setFormValidationErrors,
    errors,
    clearError,
    clearAllErrors,
    setErrors,
    hasErrors,
  };
}

/**
 * Simplified hook for common validation scenarios
 * Pre-configured for most common form fields
 */
export function useCommonFormValidation() {
  return useFormValidation([
    'name',
    'description',
    'email',
    'firstname',
    'lastname',
    'role',
    'language',
    'interval',
    'notificationLeadTime',
    'maintenanceType',
    'date',
    'file'
  ]);
}